'use client';

import { useMemo } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { useUserTenants } from '../../hooks/api/useUserTenants';
import { useOpenWorkspace } from '../../hooks/useOpenWorkspace';
import { useRecentWorkspaces } from '../../hooks/useRecentWorkspaces';
import type { WorkspaceSummary } from '../../electron-shared/workspace/workspace-descriptor';
import { ProfileMenu } from '../layout/profile/ProfileMenu';
import { ChooseTargetSection } from './launcher/ChooseTargetSection';
import { JumpBackInSection } from './launcher/JumpBackInSection';
import { LauncherBrandHeader } from './launcher/LauncherBrandHeader';

export function WorkspacePickerScreen() {
  const { tenants, currentTenantId } = useUserTenants();
  const tenantId = currentTenantId ?? null;
  const tenantName =
    tenants.find((tenant) => tenant.id === tenantId)?.name ?? '';
  const canSwitchTenant = tenants.length > 1;
  const { openWorkspace, openWorkspaceTarget } = useOpenWorkspace({
    tenantId,
    tenantName,
  });
  const { workspaces, isLoaded, forget } = useRecentWorkspaces();
  const openTargetIds = useMemo(
    () =>
      new Set(
        workspaces
          .filter(
            (workspace) => workspace.isOpen && workspace.tenantId === tenantId,
          )
          .map((workspace) => workspace.targetId),
      ),
    [tenantId, workspaces],
  );
  const openRecent = ({
    tenantId: recentTenantId,
    tenantName: recentTenantName,
    targetId,
    targetType,
    targetName,
  }: WorkspaceSummary) =>
    void openWorkspaceTarget({
      tenantId: recentTenantId,
      tenantName: recentTenantName,
      targetId,
      targetType,
      targetName,
    });

  return (
    <Box h="100vh" w="100vw" overflowY="auto" bg="bg.neutral.default">
      <VStack
        gap="8"
        maxW="560px"
        mx="auto"
        px="6"
        pt="20"
        pb="10"
        alignItems="stretch"
      >
        <LauncherBrandHeader />
        {isLoaded && (
          <JumpBackInSection
            workspaces={workspaces}
            showTenantName={canSwitchTenant}
            onOpen={openRecent}
            onRemove={(workspaceId) => void forget(workspaceId)}
          />
        )}
        <ChooseTargetSection
          tenants={tenants}
          currentTenantId={tenantId}
          tenantName={tenantName}
          recents={workspaces}
          openTargetIds={openTargetIds}
          onOpen={(target) => void openWorkspace(target)}
        />
      </VStack>
      <Box position="fixed" left="2" bottom="2" w="240px">
        <ProfileMenu collapsed={false} scope="launcher" />
      </Box>
    </Box>
  );
}
